import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import {
  AmountAndTotal,
  Attachment,
  Checklist,
  ChecklistAlert,
  ChecklistSendRequest,
  ChecklistSummaries,
  Client,
  ClientInvoicesRequest,
  ClientRevenueSummaryGridRequest,
  ClientRevenueSummaryGridResponse,
  ClientSummary,
  ClientWarningRequest,
  Company,
  Contact,
  CreateInventorySaleEntry,
  CreateInventoryStockItemForInternalUsageRequest,
  CreateInvoiceRequest,
  CreateMaintenanceHistoryItemEntry,
  CurrencyExchangeRateDto,
  DeleteInventoryStockItemsRequest,
  EmailSendResponse,
  ErrorResponse,
  Event,
  FilterRequest,
  FinalizeItemAlertsRequest,
  FinalizeSummariesRequest,
  GetAmountAndTotalForOverrideRequest,
  GetCategoriesResponse,
  GetElapsedAmountRequest,
  GetElapsedAmountResponse,
  GetEventsRequest,
  GetInventoryStockItemRequest,
  GetInventoryStocksRequest,
  GetInvoicesRequest,
  GetTotalForOverrideRequest,
  GridRequest,
  IncomingInvoice,
  IncomingInvoiceItem,
  IncomingInvoiceTotal,
  IncomingInvoiceTypes,
  InternallyUsedInventoryStockResponse,
  InventorySale,
  InventorySaleEntry,
  InventorySaleNumber,
  InventoryStock,
  InventoryStockItem,
  InventoryTransportLetter,
  Invoice,
  InvoicesSummary,
  Item,
  ItemAlert,
  LoginResponse,
  MaintenanceHistory,
  MaintenanceHistoryResponse,
  MaintenanceSheet,
  MaintenanceTodos,
  MaintenanceTypesResponse,
  MoveInventoryStockItemsRequest,
  PageResponseEnvelope,
  Partner,
  Rental,
  RentalDocumentSendout,
  RentalDocumentSendRequest,
  RentalDocumentType,
  RentalGridItem,
  RentalItem,
  RentalSummaryRequest,
  RentalSummaryResponse,
  Returnal,
  ReturnalDocumentSendout,
  ReturnalDocumentSendRequest,
  ReturnalDocumentType,
  RevenueSummaryRequest,
  RevenueSummaryResponse,
  RevenueTotal,
  RevenueTotalRequest,
  RevertFinalizedSummaryRequest,
  SaveChecklist,
  SaveClientResponse,
  SaveInMaintenanceRequest,
  SaveInventorySaleRequest,
  SaveInventoryStockItemCostRequest,
  SaveInventoryStockItemRequest,
  SaveInventoryStockResponse,
  SaveInventoryTransportLetterRequest,
  SaveInventoryTransportLetterResponse,
  SaveInvoiceRequest,
  SaveItemAlertRequest,
  SaveMaintenanceHistoryCost,
  SaveRentalResponse,
  SaveReturnalResponse,
  SaveTodoRequest,
  SaveUserRequest,
  SaveWarehouseRequest,
  SetToBeTransferredRequest,
  SetTransferredRequest,
  Site,
  Total,
  TransportDto,
  User,
  Warehouse,
  YearMonthFilter,
} from './dtos/dtos';

import {IGlobalErrorHandler} from '../common/GlobalErrorHandler';
import {
  ClientRentalSummaryGridRequest,
  ClientRentalSummaryGridResponse,
  DeleteOverrideRequest,
  SaveOverrideRequest
} from "./dtos/summaries";
import {
  CategoryDistributionStatistics,
  CategoryRangeStatistics,
  InvoiceTypeDistributionStatistics,
  ItemRangeStatistics,
  StatisticsRequest,
  TotalCategoryCostStatistics,
  TotalItemCostStatistics
} from "./dtos/statistics";
import {getAuthorizedConfig} from './auth';
import {toApiDateFormat} from "../utils/date-utils";
import Config from "../config";

interface AuthApi {
  readonly logIn: (userName: string, password: string) => Promise<LoginResponse>;
}

interface AlertsApi {
  readonly getAll: (request: GridRequest) => Promise<PageResponseEnvelope<ItemAlert>>;
  readonly create: (request: SaveItemAlertRequest) => Promise<void>;
  readonly update: (request: SaveItemAlertRequest, id: number) => Promise<void>;
  readonly delete: (id: number) => Promise<void>;
  readonly finalize: (request: FinalizeItemAlertsRequest) => Promise<void>;
  readonly getTriggeredCount: () => Promise<number>;
}

interface EventAlertsApi {
  readonly getEventAlerts: (request: GridRequest) => Promise<PageResponseEnvelope<Event>>;
  readonly getUnhandledEventCount: () => Promise<number>;
}

interface ItemsApi {
  readonly getAll: (request: GridRequest) => Promise<PageResponseEnvelope<Item>>;
  readonly get: (id: number) => Promise<Item>;
  readonly delete: (id: number, deleteDate: string) => Promise<void>;
  readonly create: (item: Item) => Promise<void>;
  readonly save: (item: Item, id?: number) => Promise<void>;
  readonly getCategories: () => Promise<GetCategoriesResponse>;

  readonly getActiveRental: (itemId: number) => Promise<Rental>

  readonly uploadAttachmentsUrl: (id: number) => string;
  readonly getAttachments: (id: number) => Promise<Attachment[]>;
  readonly deleteAttachment: (id: number, attachmentId: number) => Promise<void>;

  readonly getMaintenanceSheet: (itemId: number) => Promise<MaintenanceSheet>
  readonly saveMaintenanceSheet: (itemId: number, maintenance: MaintenanceSheet) => Promise<void>

  readonly getItemMaintenanceHistory: (itemId: number, request: GridRequest) => Promise<MaintenanceHistoryResponse>

  readonly setInMaintenance: (itemId: number, request: SaveInMaintenanceRequest) => Promise<void>
  readonly getInternallyUsedInventoryStocks: (itemId: number, request: GridRequest) => Promise<InternallyUsedInventoryStockResponse>

  readonly getMaintenanceTodos: (itemId: number) => Promise<MaintenanceTodos>
  readonly saveMaintenanceTodos: (itemId: number, todos: MaintenanceTodos) => Promise<void>
}

interface MaintenanceApi {
  readonly getUnhandledMaintenanceCount: () => Promise<number>;
  readonly getMaintenanceTypes: () => Promise<MaintenanceTypesResponse>;
  readonly getAllMaintenanceHistory: (request: GridRequest) => Promise<PageResponseEnvelope<MaintenanceHistory>>
  readonly getMaintenanceHistoryItem: (historyId: number) => Promise<MaintenanceHistory>

  readonly createMaintenanceHistoryItem: (item: MaintenanceHistory) => Promise<number>;
  readonly saveMaintenanceHistoryItem: (item: MaintenanceHistory, id: number) => Promise<number>;
  readonly deleteMaintenanceHistoryItem: (id: number) => Promise<void>;
  readonly handleMaintenanceHistoryItem: (id: number, handled: boolean) => Promise<void>;

  readonly finalizeMaintenanceHistory: (id: number) => Promise<void>
  readonly revertFinalizedMaintenanceHistory: (id: number) => Promise<void>

  readonly addMaintenanceHistoryEntries: (historyId: number, entries: CreateMaintenanceHistoryItemEntry[]) => Promise<void>;
  readonly deleteMaintenanceHistoryEntry: (historyId: number, entryId: number) => Promise<void>;
  readonly saveMaintenanceHistoryEntryCost: (historyId: number, entryId: number, request: SaveMaintenanceHistoryCost) => Promise<void>;

  readonly getMaintenanceHistoryAttachments: (historyId: number) => Promise<Attachment[]>;
  readonly uploadMaintenanceHistoryAttachmentUrl: (historyId: number) => string;
  readonly deleteMaintenanceHistoryAttachment: (historyId: number, attachmentId: number) => Promise<void>;

}

interface ClientsApi {
  readonly getAll: (request: GridRequest) => Promise<PageResponseEnvelope<ClientSummary>>;
  readonly get: (id: number) => Promise<Client>;
  readonly create: (client: Client) => Promise<SaveClientResponse>;
  readonly save: (client: Client, id?: number) => Promise<SaveClientResponse>;
  readonly setWarning: (id: number, request: ClientWarningRequest) => Promise<void>;

  readonly getContacts: (clientId: number) => Promise<Contact[]>;
  readonly filterContacts: (clientId: number, request: GridRequest) => Promise<PageResponseEnvelope<Contact>>;
  readonly createContact: (clientId: number, contact: Contact) => Promise<number>;
  readonly saveContact: (clientId: number, contact: Contact, id: number) => Promise<number>;
  readonly deleteContact: (clientId: number, id: number) => Promise<void>;

  readonly getAllSites: (clientId: number, request: GridRequest) => Promise<PageResponseEnvelope<Site>>;
  readonly createSite: (clientId: number, resp: Site) => Promise<void>;
  readonly saveSite: (clientId: number, resp: Site, id: number) => Promise<void>;
  readonly deleteSite: (clientId: number, id: number) => Promise<void>;
}

interface RentalsApi {
  readonly create: (rental: Rental) => Promise<SaveRentalResponse>;
  readonly save: (rental: Rental) => Promise<SaveRentalResponse>;
  readonly get: (id: number) => Promise<Rental>;
  readonly delete: (id: number) => Promise<void>;
  readonly getTransport: (rentalId: number) => Promise<TransportDto | null>;
  readonly saveTransport: (rentalId: number, request: TransportDto) => Promise<void>;

  readonly getAttachments: (id: number) => Promise<Attachment[]>;
  readonly uploadAttachmentsUrl: (id: number) => string;
  readonly deleteAttachment: (id: number, attachmentId: number) => Promise<void>;
}

interface RentalItemsApi {
  readonly getAll: (request: GridRequest) => Promise<PageResponseEnvelope<RentalGridItem>>;
  readonly getOngoingForClient: (clientId: number, request: GridRequest) => Promise<PageResponseEnvelope<RentalItem>>;
  readonly getElapsedAmount: (rentalItemId: number, request: GetElapsedAmountRequest) => Promise<GetElapsedAmountResponse>;
}

interface ReturnsApi {
  readonly create: (ret: Returnal) => Promise<SaveReturnalResponse>;
  readonly save: (ret: Returnal) => Promise<SaveReturnalResponse>;
  readonly get: (id: number) => Promise<Returnal>;
  readonly getTransport: (returnalId: number) => Promise<TransportDto | null>;
  readonly saveTransport: (returnalId: number, request: TransportDto) => Promise<void>;

  readonly getAttachments: (id: number) => Promise<Attachment[]>;
  readonly uploadAttachmentsUrl: (id: number) => string;
  readonly deleteAttachment: (id: number, attachmentId: number) => Promise<void>;
}

interface SummariesApi {
  readonly getClientRevenueSummaries: (request: ClientRevenueSummaryGridRequest) => Promise<ClientRevenueSummaryGridResponse>;
  readonly getRevenueSummaries: (request: RevenueSummaryRequest) => Promise<RevenueSummaryResponse>;
  readonly getRevenueTotal: (request: RevenueTotalRequest) => Promise<RevenueTotal>;
  readonly getRentalSummaries: (request: RentalSummaryRequest) => Promise<RentalSummaryResponse>;
  readonly getClientRentalSummaries: (request: ClientRentalSummaryGridRequest) => Promise<ClientRentalSummaryGridResponse>;
  readonly exportClientRentalSummaries: (request: ClientRentalSummaryGridRequest) => Promise<ArrayBuffer>;

  readonly finalizeSummaries: (request: FinalizeSummariesRequest) => Promise<void>;
  readonly revertFinalizedSummary: (request: RevertFinalizedSummaryRequest) => Promise<void>;

  readonly getAmountAndTotalForOverride: (request: GetAmountAndTotalForOverrideRequest) => Promise<AmountAndTotal>;
  readonly getTotalForOverride: (request: GetTotalForOverrideRequest) => Promise<Total>;
  readonly saveOverride: (request: SaveOverrideRequest) => Promise<void>;
  readonly deleteOverride: (request: DeleteOverrideRequest) => Promise<void>;

  readonly saveTodo: (request: SaveTodoRequest) => Promise<void>;
}

interface StatisticsApi {
  readonly getItemStatistics: (itemId: number, request: StatisticsRequest) => Promise<ItemRangeStatistics>;
  readonly getTotalItemCostStatistics: (itemId: number) => Promise<TotalItemCostStatistics>;
  readonly getCategoryStatistics: (category: string, request: StatisticsRequest) => Promise<CategoryRangeStatistics>;
  readonly getTotalCategoryCostStatistics: (category: string) => Promise<TotalCategoryCostStatistics>;

  readonly getClientStatistics: (clientId: number, request: StatisticsRequest) => Promise<CategoryDistributionStatistics>;
  readonly getOverallStatistics: (request: StatisticsRequest) => Promise<CategoryDistributionStatistics>;
  readonly getIncomingInvoiceStatistics: (request: StatisticsRequest) => Promise<InvoiceTypeDistributionStatistics>
}

interface InvoicingApi {
  readonly getInvoices: (request: GetInvoicesRequest) => Promise<PageResponseEnvelope<InvoicesSummary>>;
  readonly getClientInvoices: (request: ClientInvoicesRequest) => Promise<PageResponseEnvelope<Invoice>>;
  readonly createInvoice: (request: CreateInvoiceRequest) => Promise<number>;
  readonly saveInvoice: (id: number, request: SaveInvoiceRequest) => Promise<number>;
  readonly getInvoice: (id: number) => Promise<Invoice>;
  readonly deleteInvoice: (id: number) => Promise<void>;
  readonly sendInvoice: (id: number) => Promise<void>;
  readonly stornoInvoice: (id: number) => Promise<void>;
}

interface IncomingInvoicesApi {
  readonly getTotals: (request: FilterRequest) => Promise<IncomingInvoiceTotal>;
  readonly getTypes: () => Promise<IncomingInvoiceTypes>;
  readonly getAll: (request: GridRequest) => Promise<PageResponseEnvelope<IncomingInvoice>>;
  readonly create: (invoice: IncomingInvoice) => Promise<IncomingInvoice>;
  readonly save: (invoice: IncomingInvoice, id: number) => Promise<IncomingInvoice>;
  readonly get: (id: number) => Promise<IncomingInvoice>;
  readonly reopen: (id: number) => Promise<IncomingInvoice>;
  readonly delete: (id: number) => Promise<void>;
  readonly setToBeTransferred: (request: SetToBeTransferredRequest) => Promise<void>;
  readonly setTransferred: (request: SetTransferredRequest) => Promise<void>;
  readonly exportToBeTransferredItems: (filename: string, startDate?: string, endDate?: string) => Promise<ArrayBuffer>;

  readonly searchPartners: (value: string) => Promise<Partner[]>;
  readonly deletePartner: (id: number) => Promise<void>;

  readonly uploadAttachmentsUrl: (id: number) => string;
  readonly getAttachments: (id: number) => Promise<Attachment[]>;
  readonly deleteAttachment: (id: number, fileName: string) => Promise<void>;

  readonly getItems: (id: number) => Promise<Item[]>;
  readonly addItem: (id: number, itemId: number) => Promise<Item[]>;
  readonly removeItem: (id: number, itemId: number) => Promise<Item[]>;
  readonly handleItem: (id: number, itemId: number, handled: boolean) => Promise<void>;
  readonly getAllByItem: (itemId: number, request: GridRequest) => Promise<PageResponseEnvelope<IncomingInvoiceItem>>;
}

interface EventsApi {
  readonly create: (event: Event) => Promise<void>;
  readonly save: (eventId: number, event: Event) => Promise<void>;
  readonly delete: (eventId: number) => Promise<void>;
  readonly get: (eventId: number) => Promise<Event>;

  readonly getEvents: (request: GetEventsRequest) => Promise<Event[]>
  readonly getClientEvents: (clientId: number, request: GridRequest) => Promise<PageResponseEnvelope<Event>>;

  readonly getAttachments: (id: number) => Promise<Attachment[]>;
  readonly uploadAttachmentsUrl: (id: number) => string;
  readonly deleteAttachment: (id: number, attachmentId: number) => Promise<void>;
}

interface InventorySalesApi {
  readonly create: (request: SaveInventorySaleRequest) => Promise<number>;
  readonly save: (saleId: number, request: SaveInventorySaleRequest) => Promise<void>;
  readonly delete: (saleId: number) => Promise<void>;
  readonly get: (saleId: number) => Promise<InventorySale>;

  readonly addEntries: (saleId: number, entries: CreateInventorySaleEntry[]) => Promise<void>;
  readonly deleteEntry: (saleId: number, entryId: number) => Promise<void>;
  readonly saveEntry: (saleId: number, entryId: number, entry: InventorySaleEntry) => Promise<void>;

  readonly getSales: (request: GridRequest) => Promise<PageResponseEnvelope<InventorySale>>
  readonly getAllSaleNumbers: () => Promise<InventorySaleNumber[]>
}

interface UsersApi {
  readonly getMyCompanies: () => Promise<Company[]>;
  readonly getSelectedCompanyInfo: () => Promise<Company>;
  readonly getSalesUsers: () => Promise<User[]>;
  readonly getUsers: () => Promise<User[]>;
  readonly create: (request: SaveUserRequest) => Promise<void>;
  readonly update: (request: SaveUserRequest) => Promise<void>;
}

interface WarehousesApi {
  readonly getWarehouses: () => Promise<Warehouse[]>;
  readonly saveWarehouse: (request: SaveWarehouseRequest) => Promise<void>;
}

interface CurrencyApi {
  readonly getRate: (code: string, date: string) => Promise<CurrencyExchangeRateDto>;
}

interface InventoryStocksApi {
  readonly getAll: (request: GetInventoryStocksRequest) => Promise<PageResponseEnvelope<InventoryStock>>;
  readonly get: (id: number) => Promise<InventoryStock>;
  readonly create: (stock: InventoryStock) => Promise<SaveInventoryStockResponse>;
  readonly save: (stock: InventoryStock, id: number) => Promise<SaveInventoryStockResponse>;

  readonly getNames: () => Promise<string[]>;
  readonly getTypes: () => Promise<string[]>;
  readonly getBrands: () => Promise<string[]>;
  readonly getInfinite: (request: GridRequest) => Promise<PageResponseEnvelope<InventoryStock>>;
}

interface InventoryStockItemsApi {
  readonly getAll: (request: GetInventoryStockItemRequest) => Promise<PageResponseEnvelope<InventoryStockItem>>;
  readonly create: (request: SaveInventoryStockItemRequest) => Promise<void>;
  readonly save: (id: number, request: SaveInventoryStockItemCostRequest) => Promise<void>;
  readonly createForInternalUsage: (request: CreateInventoryStockItemForInternalUsageRequest) => Promise<void>;
  readonly delete: (request: DeleteInventoryStockItemsRequest) => Promise<void>;
  readonly move: (request: MoveInventoryStockItemsRequest) => Promise<void>;
  readonly getAllByUser: (request: GridRequest) => Promise<PageResponseEnvelope<InventoryStockItem>>;
  readonly getForSale: (request: GridRequest) => Promise<PageResponseEnvelope<InventoryStockItem>>;
}

interface InventoryTransportLettersApi {
  readonly getAll: (request: GridRequest) => Promise<PageResponseEnvelope<InventoryTransportLetter>>;
  readonly get: (id: number) => Promise<InventoryTransportLetter>;
  readonly create: (request: SaveInventoryTransportLetterRequest) => Promise<number>;
  readonly save: (id: number, request: SaveInventoryTransportLetterRequest) => Promise<SaveInventoryTransportLetterResponse>;
  readonly delete: (id: number) => Promise<void>;

  readonly uploadAttachmentsUrl: (id: number) => string;
  readonly getAttachments: (id: number) => Promise<Attachment[]>;
  readonly deleteAttachment: (id: number, fileName: string) => Promise<void>;

}

interface ChecklistApi {
  readonly create: (checklist: SaveChecklist) => Promise<number>;
  readonly save: (checklistId: number, checklist: SaveChecklist) => Promise<void>;
  readonly delete: (checklistId: number) => Promise<void>;
  readonly get: (checklistId: number) => Promise<Checklist>;

  readonly uploadAttachment: (checklistId: number, file: Blob) => Promise<Attachment>;
  readonly deleteAttachment: (checklistId: number, attachmentId: number) => Promise<void>;

  readonly getRentalChecklists: (rentalId: number) => Promise<ChecklistSummaries>;
  readonly sendRentalChecklists: (rentalId: number, request: ChecklistSendRequest) => Promise<EmailSendResponse>;
  readonly getReturnalChecklists: (returnalId: number) => Promise<ChecklistSummaries>;
  readonly sendReturnalChecklists: (returnalId: number, request: ChecklistSendRequest) => Promise<EmailSendResponse>;
}

interface ChecklistAlertsApi {
  readonly getRentalChecklistAlerts: (request: GridRequest) => Promise<PageResponseEnvelope<ChecklistAlert>>;
  readonly getReturnalChecklistAlerts: (request: GridRequest) => Promise<PageResponseEnvelope<ChecklistAlert>>;
  readonly getUnhandledChecklistCount: () => Promise<number>;
}

interface DocumentApi {
  readonly getRentalDocumentSendouts: (rentalId: number) => Promise<RentalDocumentSendout[]>;
  readonly sendRentalDocuments: (rentalId: number, request: RentalDocumentSendRequest) => Promise<EmailSendResponse>;
  readonly getRentalDocument: (rentalId: number, type: RentalDocumentType) => Promise<ArrayBuffer>;

  readonly getReturnalDocumentSendouts: (rentalId: number) => Promise<ReturnalDocumentSendout[]>;
  readonly sendReturnalDocuments: (rentalId: number, request: ReturnalDocumentSendRequest) => Promise<EmailSendResponse>;
  readonly getReturnalDocument: (returnalId: number, type: ReturnalDocumentType) => Promise<ArrayBuffer>;
}

class FuroApi {
  public globalErrorHandler?: IGlobalErrorHandler | null;

  public readonly auth: AuthApi = {
    logIn: (userName: string, password: string): Promise<LoginResponse> => {
      const body = {userName, password};
      return this.axios
        .post(`/login`, body)
        .then((response: AxiosResponse<LoginResponse>) => {
          return response.data;
        });
    },
  };

  public readonly alerts: AlertsApi = {
    getAll: (request: GridRequest): Promise<PageResponseEnvelope<ItemAlert>> => this.doPostWithAuth(`alerts`, request),
    create: (request: SaveItemAlertRequest): Promise<void> => this.doPostWithAuth(`/alert`, request),
    update: (request: SaveItemAlertRequest, id: number): Promise<void> => this.doPostWithAuth(`/alert/${id}`, request),
    delete: (id: number): Promise<void> => this.doDeleteWithAuth(`/alert/${id}`),

    finalize: (request: FinalizeItemAlertsRequest): Promise<void> => this.doPostWithAuth(`alerts/finalize`, request),

    getTriggeredCount: (): Promise<number> => this.doGetWithAuth(`alerts`)
  };

  public readonly checklistAlerts: ChecklistAlertsApi = {
    getRentalChecklistAlerts: (request: GridRequest): Promise<PageResponseEnvelope<ChecklistAlert>> => this.doPostWithAuth("checklist/alerts/rental", request),
    getReturnalChecklistAlerts: (request: GridRequest): Promise<PageResponseEnvelope<ChecklistAlert>> => this.doPostWithAuth("checklist/alerts/returnal", request),
    getUnhandledChecklistCount: (): Promise<number> => this.doGetWithAuth("checklist/alerts/unhandled-count"),
  };

  public readonly eventAlerts: EventAlertsApi = {
    getEventAlerts: (request: GridRequest): Promise<PageResponseEnvelope<Event>> => this.doPostWithAuth(`events/alerts`, request),
    getUnhandledEventCount: (): Promise<number> => this.doGetWithAuth(`events/alerts`),
  };

  public readonly items: ItemsApi = {
    getAll: (request: GridRequest): Promise<PageResponseEnvelope<Item>> => this.doPostWithAuth(`/items`, request),
    get: (id: number): Promise<Item> => this.doGetWithAuth(`/item/${id}`),
    delete: (id: number, deleted: string): Promise<void> => this.doPostWithAuth(`/item/${id}/delete`, {deleted}),
    create: (item: Item): Promise<void> => this.doPostWithAuth('/item', item),
    save: (item: Item, id?: number): Promise<void> => this.doPostWithAuth(`/item/${id}`, item),
    getCategories: (): Promise<GetCategoriesResponse> => this.doGetWithAuth('/items/categories'),

    getMaintenanceSheet: (itemId: number): Promise<MaintenanceSheet> => this.doGetWithAuth(`/item/${itemId}/maintenance`),
    saveMaintenanceSheet: (itemId: number, maintenance: MaintenanceSheet) => this.doPostWithAuth(`/item/${itemId}/maintenance`, maintenance),

    getMaintenanceTodos: (itemId: number): Promise<MaintenanceTodos> => this.doGetWithAuth(`/item/${itemId}/maintenance/todos`),
    saveMaintenanceTodos: (itemId: number, todos: MaintenanceTodos) => this.doPostWithAuth(`/item/${itemId}/maintenance/todos`, todos),
    getInternallyUsedInventoryStocks: (itemId: number, request: GridRequest) =>
      this.doPostWithAuth(`/item/${itemId}/maintenance/internally-used-inventory-stocks`, request),

    setInMaintenance: (itemId: number, request: SaveInMaintenanceRequest) => this.doPostWithAuth(`/item/${itemId}/maintenance/set-in-maintenance`, request),

    getActiveRental: (itemId: number) => this.doGetWithAuth(`/item/${itemId}/active-rental`),

    getItemMaintenanceHistory: (itemId: number, request: GridRequest) => this.doPostWithAuth(`/item/${itemId}/maintenance/history`, request),

    deleteAttachment: (id: number, attachmentId: number) => this.doDeleteWithAuth(`/item/${id}/attachments/${attachmentId}`),
    getAttachments: (id: number) => this.doGetWithAuth(`/item/${id}/attachments`),
    uploadAttachmentsUrl: (id: number) => `${this.axios.defaults.baseURL}/item/${id}/attachments`,
  };

  public readonly maintenance: MaintenanceApi = {
    getUnhandledMaintenanceCount: () => this.doGetWithAuth(`/maintenances/unhandled-count`),
    getMaintenanceTypes: (): Promise<MaintenanceTypesResponse> => this.doGetWithAuth('/maintenances/types'),

    getAllMaintenanceHistory: (request: GridRequest) => this.doPostWithAuth(`/maintenances`, request),
    getMaintenanceHistoryItem: (historyId: number) => this.doGetWithAuth(`/maintenances/${historyId}`),

    createMaintenanceHistoryItem: (historyItem: MaintenanceHistory) => this.doPostWithAuth(`/maintenance`, historyItem),
    saveMaintenanceHistoryItem: (historyItem: MaintenanceHistory, id: number) => this.doPostWithAuth(`/maintenance/${id}`, historyItem),
    deleteMaintenanceHistoryItem: (id: number) => this.doDeleteWithAuth(`/maintenance/${id}`),
    handleMaintenanceHistoryItem: (id: number, handled: boolean) => this.doPostWithAuth(`/maintenance/${id}/handle`, {handled}),

    addMaintenanceHistoryEntries: (historyId: number, entries: CreateMaintenanceHistoryItemEntry[]) => this.doPostWithAuth(`/maintenance/${historyId}/entries`, entries),
    deleteMaintenanceHistoryEntry: (historyId: number, entryId: number) => this.doDeleteWithAuth(`/maintenance/${historyId}/entries/${entryId}`),
    saveMaintenanceHistoryEntryCost: (historyId: number, entryId: number, request: SaveMaintenanceHistoryCost) => this.doPostWithAuth(`/maintenance/${historyId}/entries/${entryId}`, request),

    getMaintenanceHistoryAttachments: (historyId: number) => this.doGetWithAuth(`/maintenance/${historyId}/attachments`),
    uploadMaintenanceHistoryAttachmentUrl: (historyId: number) => `${this.axios.defaults.baseURL}/maintenance/${historyId}/attachments`,
    deleteMaintenanceHistoryAttachment: (historyId: number, attachmentId: number) => this.doDeleteWithAuth(`/maintenance/${historyId}/attachments/${attachmentId}`),

    finalizeMaintenanceHistory: (historyId: number) =>
      this.doPostWithAuth(`/maintenance/${historyId}/finalize`, null),
    revertFinalizedMaintenanceHistory: (historyId: number) =>
      this.doPostWithAuth(`/maintenance/${historyId}/revert-finalize`, null),
  }

  public readonly clients: ClientsApi = {
    getAll: (request: GridRequest): Promise<PageResponseEnvelope<ClientSummary>> => this.doPostWithAuth('/clients', request),
    get: (id: number): Promise<Client> => this.doGetWithAuth(`/client/${id}`),
    create: (client: Client): Promise<SaveClientResponse> => this.doPostWithAuth('/client', client),
    save: (client: Client, id?: number): Promise<SaveClientResponse> => this.doPostWithAuth(`/client/${id}`, client),
    setWarning: (id: number, request: ClientWarningRequest) => this.doPostWithAuth(`/client/${id}/set-warning`, request),

    // Client contacts
    getContacts: (clientId: number) => this.doGetWithAuth(`/client/${clientId}/contacts`),
    filterContacts: (clientId: number, request: GridRequest) => this.doPostWithAuth(`/client/${clientId}/contacts`, request),
    createContact: (clientId: number, contact: Contact) => this.doPostWithAuth(`/client/${clientId}/contact`, contact),
    saveContact: (clientId: number, contact: Contact, id: number) => this.doPostWithAuth(`/client/${clientId}/contact/${id}`, contact),
    deleteContact: (clientId: number, id: number) => this.doDeleteWithAuth(`/client/${clientId}/contact/${id}`),

    // Client sites
    getAllSites: (clientId: number, request: GridRequest) => this.doPostWithAuth(`/client/${clientId}/sites`, request),
    createSite: (clientId: number, site: Site) => this.doPostWithAuth(`/client/${clientId}/site`, site),
    saveSite: (clientId: number, site: Site, id: number) => this.doPostWithAuth(`/client/${clientId}/site/${id}`, site),
    deleteSite: (clientId: number, id: number) => this.doDeleteWithAuth(`/client/${clientId}/site/${id}`),
  };

  public readonly rentals: RentalsApi = {
    create: (rental: Rental): Promise<SaveRentalResponse> => this.doPostWithAuth('/rental', rental),
    save: (rental: Rental): Promise<SaveRentalResponse> => this.doPostWithAuth(`/rental/${rental.id}`, rental),
    get: (id: number): Promise<Rental> => this.doGetWithAuth(`/rental/${id}`),
    delete: (id: number) => this.doDeleteWithAuth(`/rental/${id}`),
    getTransport: (rentalId: number): Promise<TransportDto | null> => this.doNullableGetWithAuth(`/rental/${rentalId}/transport`),
    saveTransport: (rentalId: number, request: TransportDto): Promise<void> => this.doPostWithAuth(`/rental/${rentalId}/transport`, request),

    getAttachments: (id: number) => this.doGetWithAuth(`/rental/${id}/attachments`),
    uploadAttachmentsUrl: (id: number) => `${this.axios.defaults.baseURL}/rental/${id}/attachments`,

    deleteAttachment: (id: number, attachmentId: number) => this.doDeleteWithAuth(`/rental/${id}/attachments/${attachmentId}`),
  };

  public readonly rentalItems: RentalItemsApi = {
    getOngoingForClient: (clientId: number, request: GridRequest): Promise<PageResponseEnvelope<RentalItem>> =>
      this.doPostWithAuth(`/rental-items/ongoing?clientId=${clientId}`, request),

    getAll: (request: GridRequest): Promise<PageResponseEnvelope<RentalGridItem>> =>
      this.doPostWithAuth(`/rental-items`, request),

    getElapsedAmount: (rentalItemId: number, request: GetElapsedAmountRequest): Promise<GetElapsedAmountResponse> =>
      this.doPostWithAuth(`/rental-item/${rentalItemId}/elapsed-amount`, request),
  };

  public readonly returns: ReturnsApi = {
    create: (ret: Returnal): Promise<SaveReturnalResponse> => this.doPostWithAuth('/returnal', ret),
    save: (ret: Returnal): Promise<SaveReturnalResponse> => this.doPostWithAuth(`/returnal/${ret.id}`, ret),
    get: (id: number): Promise<Returnal> => this.doGetWithAuth(`/returnal/${id}`),
    getTransport: (returnalId: number): Promise<TransportDto | null> => this.doNullableGetWithAuth(`/returnal/${returnalId}/transport`),
    saveTransport: (returnalId: number, request: TransportDto): Promise<void> => this.doPostWithAuth(`/returnal/${returnalId}/transport`, request),

    getAttachments: (id: number) => this.doGetWithAuth(`/returnal/${id}/attachments`),
    uploadAttachmentsUrl: (id: number) => `${this.axios.defaults.baseURL}/returnal/${id}/attachments`,
    deleteAttachment: (id: number, attachmentId: number) => this.doDeleteWithAuth(`/returnal/${id}/attachments/${attachmentId}`),
  };

  public readonly summaries: SummariesApi = {
    getClientRevenueSummaries: (request: ClientRevenueSummaryGridRequest): Promise<ClientRevenueSummaryGridResponse> =>
      this.doPostWithAuth('/revenue-summary/client', this.alignMonthIndex(request)),

    getRevenueSummaries: (request: RevenueSummaryRequest): Promise<RevenueSummaryResponse> =>
      this.doPostWithAuth('/revenue-summary', this.alignMonthIndex(request)),

    getRevenueTotal: (request: RevenueTotalRequest): Promise<RevenueTotal> =>
      this.doPostWithAuth('/revenue-summary/total', this.alignMonthIndex(request)),


    getRentalSummaries: (request: RentalSummaryRequest): Promise<RentalSummaryResponse> =>
      this.doPostWithAuth('/summary', this.alignMonthIndex(request)),

    getClientRentalSummaries: (request: ClientRentalSummaryGridRequest): Promise<ClientRentalSummaryGridResponse> =>
      this.doPostWithAuth('/summary/client', this.alignMonthIndex(request)),

    exportClientRentalSummaries: (request: ClientRentalSummaryGridRequest): Promise<ArrayBuffer> => {
      const r = this.alignMonthIndex(request);
      const params = [
        this.serialize("clientId", String(r.clientId)),
        this.serialize("year", String(r.year)),
        this.serialize("month", String(r.month)),
      ];
      return this.doArrayBufferGetWithAuth(`/summary/client/export?${params.join('&')}`);
    },

    getAmountAndTotalForOverride: (request: GetAmountAndTotalForOverrideRequest): Promise<AmountAndTotal> =>
      this.doPostWithAuth('/summary/override/get-amount-total', this.alignMonthIndex(request)),

    getTotalForOverride: (request: GetTotalForOverrideRequest): Promise<Total> =>
      this.doPostWithAuth('/summary/override/get-total', this.alignMonthIndex(request)),

    saveOverride: (request: SaveOverrideRequest): Promise<void> =>
      this.doPostWithAuth('/summary/override/save', this.alignMonthIndex(request)),

    deleteOverride: (request: DeleteOverrideRequest): Promise<void> =>
      this.doPostWithAuth('/summary/override/delete', this.alignMonthIndex(request)),

    saveTodo: (request: SaveTodoRequest): Promise<void> =>
      this.doPostWithAuth('/summary/save-todo', this.alignMonthIndex(request)),

    finalizeSummaries: (request: FinalizeSummariesRequest) =>
      this.doPostWithAuth('/summary/finalize', this.alignMonthIndex(request)),

    revertFinalizedSummary: (request: RevertFinalizedSummaryRequest) =>
      this.doPostWithAuth('/summary/revert-finalized', this.alignMonthIndex(request))
  };

  public readonly invoicing: InvoicingApi = {
    getInvoices: (request: GetInvoicesRequest): Promise<PageResponseEnvelope<InvoicesSummary>> =>
      this.doPostWithAuth('/invoicing/invoices', this.alignMonthIndexes(request)),

    getClientInvoices: (request: ClientInvoicesRequest): Promise<PageResponseEnvelope<Invoice>> =>
      this.doPostWithAuth('/invoicing/invoices/client', this.alignMonthIndexes(request)),

    createInvoice: (request: CreateInvoiceRequest): Promise<number> =>
      this.doPostWithAuth('/invoicing', this.alignMonthIndex(request)),

    saveInvoice: (id: number, request: SaveInvoiceRequest): Promise<number> =>
      this.doPostWithAuth(`/invoicing/${id}`, request),

    getInvoice: (id: number): Promise<Invoice> =>
      this.doGetWithAuth(`/invoicing/${id}`),

    deleteInvoice: (id: number): Promise<void> =>
      this.doDeleteWithAuth(`/invoicing/${id}`),

    sendInvoice: (id: number) =>
      this.doPostWithAuth(`/invoicing/${id}/send`, {}),

    stornoInvoice: (id: number) =>
      this.doPostWithAuth(`/invoicing/${id}/storno`, {}),
  };

  public readonly incomingInvoices: IncomingInvoicesApi = {
    getTotals: (request: FilterRequest) =>
      this.doPostWithAuth('/incoming-invoices/totals', request),

    getTypes: () =>
      this.doGetWithAuth('/incoming-invoices/types'),

    getAll: (request: GridRequest): Promise<PageResponseEnvelope<IncomingInvoice>> =>
      this.doPostWithAuth('/incoming-invoices', request),

    getAllByItem: (itemId: number, request: GridRequest) =>
      this.doPostWithAuth(`/incoming-invoices/items/${itemId}`, request),

    get: (id: number): Promise<IncomingInvoice> =>
      this.doGetWithAuth(`/incoming-invoice/${id}`),

    create: (invoice: IncomingInvoice) => {
      return this.doPostWithAuth(`/incoming-invoice`, invoice)
    },

    save: (invoice: IncomingInvoice, id: number) => {
      return this.doPostWithAuth(`/incoming-invoice/${id}`, invoice)
    },

    reopen: (id: number): Promise<IncomingInvoice> =>
      this.doPostWithAuth(`/incoming-invoice/${id}/reopen`, {}),

    delete: (id: number): Promise<void> =>
      this.doDeleteWithAuth(`/incoming-invoice/${id}`),

    setToBeTransferred: (request: SetToBeTransferredRequest) =>
      this.doPostWithAuth('/incoming-invoices/set-to-be-transferred', request),

    setTransferred: (request: SetTransferredRequest) =>
      this.doPostWithAuth('/incoming-invoices/set-transferred', request),

    exportToBeTransferredItems: (filename: string, startDate?: string, endDate?: string) => {
      const params = [this.serialize("fileName", filename)];
      if (startDate) {
        params.push(this.serialize("start", startDate));
      }
      if (endDate) {
        params.push(this.serialize("end", endDate));
      }
      return this.doArrayBufferGetWithAuth(`/incoming-invoices/export/to-be-transferred?${params.join('&')}`);
    },

    searchPartners: (value: string) =>
      this.doGetWithAuth(`/incoming-invoices/partners?${this.serialize('name', value)}`),

    deletePartner: (id: number) =>
      this.doDeleteWithAuth(`/incoming-invoices/partners/${id}`),

    uploadAttachmentsUrl: (id: number) =>
      `${this.axios.defaults.baseURL}/incoming-invoice/${id}/attachments`,

    getAttachments: (id: number) =>
      this.doGetWithAuth(`/incoming-invoice/${id}/attachments`),

    deleteAttachment: (id: number, fileName: string) =>
      this.doDeleteWithAuth(`/incoming-invoice/${id}/attachments/${fileName}`),

    getItems: (id: number) =>
      this.doGetWithAuth(`/incoming-invoice/${id}/items`),

    addItem: (id: number, itemId: number) =>
      this.doPostWithAuth(`/incoming-invoice/${id}/items`, {itemId}),

    removeItem: (id: number, itemId: number) =>
      this.doDeleteWithAuth(`/incoming-invoice/${id}/items/${itemId}`),

    handleItem: (id: number, itemId: number, handled: boolean) =>
      this.doPostWithAuth(`/incoming-invoice/${id}/items/${itemId}/handle`, {handled})
  };

  public readonly statistics: StatisticsApi = {
    getItemStatistics: (itemId: number, request: StatisticsRequest) =>
      this.doPostWithAuth(`/statistics/item/${itemId}`, this.alignMonthIndexes(request)),

    getTotalItemCostStatistics: (itemId: number) => this.doGetWithAuth(`/statistics/item/${itemId}`),

    getCategoryStatistics: (category: string, request: StatisticsRequest) =>
      this.doPostWithAuth(`/statistics/category/${category}`, this.alignMonthIndexes(request)),

    getTotalCategoryCostStatistics: (category: string) => this.doGetWithAuth(`/statistics/category/${category}`),

    getClientStatistics: (clientId: number, request: StatisticsRequest) =>
      this.doPostWithAuth(`/statistics/client/${clientId}`, this.alignMonthIndexes(request)),

    getOverallStatistics: (request: StatisticsRequest) =>
      this.doPostWithAuth(`/statistics/overall`, this.alignMonthIndexes(request)),

    getIncomingInvoiceStatistics: (request: StatisticsRequest) =>
      this.doPostWithAuth('/statistics/incoming-invoice', this.alignMonthIndexes(request))
  };

  public readonly events: EventsApi = {
    create: (event: Event): Promise<void> => this.doPostWithAuth('/events', event),
    save: (eventId: number, event: Event): Promise<void> => this.doPostWithAuth(`/events/${eventId}`, event),
    delete: (eventId: number): Promise<void> => this.doDeleteWithAuth(`/events/${eventId}`),
    get: (eventId: number): Promise<Event> => this.doGetWithAuth(`/events/${eventId}`),

    getEvents: (request: GetEventsRequest): Promise<Event[]> => {
      const params = [
        this.serialize("userName", request.userName),
        this.serialize("start", toApiDateFormat(request.dateRange.start)),
        this.serialize("end", toApiDateFormat(request.dateRange.end)),
      ];
      return this.doGetWithAuth(`/events?${params.join('&')}`);
    },

    getClientEvents: (clientId: number, request: GridRequest): Promise<PageResponseEnvelope<Event>> => {
      return this.doPostWithAuth(`/events/client/${clientId}`, request);
    },

    getAttachments: (id: number) => this.doGetWithAuth(`/events/${id}/attachments`),
    uploadAttachmentsUrl: (id: number) => `${this.axios.defaults.baseURL}/events/${id}/attachments`,
    deleteAttachment: (id: number, attachmentId: number) => this.doDeleteWithAuth(`/events/${id}/attachments/${attachmentId}`),
  };

  public readonly sales: InventorySalesApi = {
    create: (request: SaveInventorySaleRequest): Promise<number> => this.doPostWithAuth('/sale', request),
    save: (saleId: number, request: SaveInventorySaleRequest): Promise<void> => this.doPostWithAuth(`/sale/${saleId}`, request),
    delete: (saleId: number): Promise<void> => this.doDeleteWithAuth(`/sale/${saleId}`),
    get: (saleId: number): Promise<InventorySale> => this.doGetWithAuth(`/sale/${saleId}`),

    addEntries: (saleId: number, entries: CreateInventorySaleEntry[]): Promise<void> => this.doPostWithAuth(`/sale/${saleId}/entries`, entries),
    deleteEntry: (saleId: number, entryId: number): Promise<void> => this.doDeleteWithAuth(`/sale/${saleId}/entries/${entryId}`),
    saveEntry: (saleId: number, entryId: number, entry: InventorySaleEntry): Promise<void> => this.doPostWithAuth(`/sale/${saleId}/entries/${entryId}`, entry),

    getSales: (request: GridRequest): Promise<PageResponseEnvelope<InventorySale>> => this.doPostWithAuth('/sales', request),
    getAllSaleNumbers: (): Promise<InventorySaleNumber[]> => this.doGetWithAuth('/sales/numbers'),
  };

  public readonly checklist: ChecklistApi = {
    create: (checklist: SaveChecklist): Promise<number> => this.doPostWithAuth('/checklist', checklist),
    save: (checklistId: number, checklist: SaveChecklist): Promise<void> => this.doPostWithAuth(`/checklist/${checklistId}`, checklist),
    delete: (checklistId: number): Promise<void> => this.doDeleteWithAuth(`/checklist/${checklistId}`),
    get: (checklistId: number): Promise<Checklist> => this.doGetWithAuth(`/checklist/${checklistId}`),

    deleteAttachment: (checklistId: number, attachmentId: number) => this.doDeleteWithAuth(`/checklist/${checklistId}/attachments/${attachmentId}`),
    uploadAttachment: (checklistId: number, file: Blob) => {
      const request = new FormData();
      request.set('file', file);
      return this.doMultipartPostWithAuth(`/checklist/${checklistId}/attachments`, request);
    },

    getRentalChecklists: (rentalId: number) => this.doGetWithAuth(`/checklist/summary/rental/${rentalId}`),
    sendRentalChecklists: (rentalId: number, request: ChecklistSendRequest) => this.doPostWithAuth(`/checklist/summary/rental/${rentalId}/send`, request),

    getReturnalChecklists: (returnalId: number) => this.doGetWithAuth(`/checklist/summary/returnal/${returnalId}`),
    sendReturnalChecklists: (returnalId: number, request: ChecklistSendRequest) => this.doPostWithAuth(`/checklist/summary/returnal/${returnalId}/send`, request),
  };

  public readonly document: DocumentApi = {
    getRentalDocumentSendouts: (rentalId: number) => this.doGetWithAuth(`/rental/${rentalId}/documents`),
    sendRentalDocuments: (rentalId: number, request: RentalDocumentSendRequest) => this.doPostWithAuth(`/rental/${rentalId}/documents`, request),
    getRentalDocument: (rentalId: number, type: RentalDocumentType) => this.doArrayBufferGetWithAuth(`${this.axios.defaults.baseURL}/rental/${rentalId}/documents/export?type=${type}`),


    getReturnalDocumentSendouts: (returnalId: number) => this.doGetWithAuth(`/returnal/${returnalId}/documents`),
    sendReturnalDocuments: (returnalId: number, request: ReturnalDocumentSendRequest) => this.doPostWithAuth(`/returnal/${returnalId}/documents`, request),
    getReturnalDocument: (returnalId: number, type: ReturnalDocumentType) => this.doArrayBufferGetWithAuth(`${this.axios.defaults.baseURL}/returnal/${returnalId}/documents/export?type=${type}`),
  };

  public readonly users: UsersApi = {
    getMyCompanies: (): Promise<Company[]> => this.doGetWithAuth('/users/me/companies'),
    getSelectedCompanyInfo: (): Promise<Company> => this.doGetWithAuth('/users/me/selected-company'),
    getSalesUsers: (): Promise<User[]> => this.doGetWithAuth('/users/sales'),

    getUsers: (): Promise<User[]> => this.doGetWithAuth('/users'),
    create: (request: SaveUserRequest): Promise<void> => this.doPostWithAuth('/users', request),
    update: (request: SaveUserRequest): Promise<void> => this.doPutWithAuth('/users', request)
  };

  public readonly warehouses: WarehousesApi = {
    getWarehouses: (): Promise<Warehouse[]> => this.doGetWithAuth('/warehouses'),
    saveWarehouse: (request: SaveWarehouseRequest): Promise<void> => this.doPostWithAuth('/warehouses', request),
  };

  public readonly currency: CurrencyApi = {
    getRate: (code: string, date: string): Promise<CurrencyExchangeRateDto> =>
      this.doGetWithAuth(`/currency?code=${encodeURIComponent(code)}&${this.serialize('date', date)}`),
  };

  public readonly inventoryStocks: InventoryStocksApi = {
    getAll: (request: GetInventoryStocksRequest): Promise<PageResponseEnvelope<InventoryStock>> => {
      return this.doPostWithAuth(`/inventory-stocks`, request);
    },
    get: (id: number): Promise<InventoryStock> => this.doGetWithAuth(`/inventory-stock/${id}`),
    create: (stock: InventoryStock): Promise<SaveInventoryStockResponse> => this.doPostWithAuth('/inventory-stock', stock),
    save: (stock: InventoryStock, id: number): Promise<SaveInventoryStockResponse> => this.doPostWithAuth(`/inventory-stock/${id}`, stock),
    getNames: (): Promise<string[]> => {
      return this.doGetWithAuth<{ names: string[] } | string[]>('/inventory-stocks/names')
        .then((response) => Array.isArray(response) ? response : (response.names || []));
    },
    getTypes: (): Promise<string[]> => {
      return this.doGetWithAuth<{ types: string[] } | string[]>('/inventory-stocks/types')
        .then((response) => Array.isArray(response) ? response : (response.types || []));
    },
    getBrands: (): Promise<string[]> => {
      return this.doGetWithAuth<{ brands: string[] } | string[]>('/inventory-stocks/brands')
        .then((response) => Array.isArray(response) ? response : (response.brands || []));
    },
    getInfinite: (request: GridRequest): Promise<PageResponseEnvelope<InventoryStock>> => {
      return this.doPostWithAuth('/inventory-stocks/infinite', request);
    },
  };

  public readonly inventoryStockItems: InventoryStockItemsApi = {
    getAll: (request: GetInventoryStockItemRequest) =>
      this.doPostWithAuth<PageResponseEnvelope<InventoryStockItem>>(`/inventory-stock-items`, request),

    create: (request: SaveInventoryStockItemRequest) =>
      this.doPostWithAuth(`/inventory-stock-items/create`, request),

    save: (id: number, request: SaveInventoryStockItemCostRequest) =>
      this.doPostWithAuth(`/inventory-stock-item/${id}`, request),

    createForInternalUsage: (request: CreateInventoryStockItemForInternalUsageRequest) =>
      this.doPostWithAuth(`/inventory-stock-items/create-for-internal-usage`, request),

    delete: (request: DeleteInventoryStockItemsRequest): Promise<void> =>
      this.doPostWithAuth(`/inventory-stock-items/delete`, request),

    move: (request: MoveInventoryStockItemsRequest): Promise<void> =>
      this.doPostWithAuth(`/inventory-stock-items/move`, request),

    getAllByUser: (request: GetInventoryStockItemRequest): Promise<PageResponseEnvelope<InventoryStockItem>> =>
      this.doPostWithAuth(`/inventory-stock-items/user`, request),

    getForSale: (request: GetInventoryStockItemRequest): Promise<PageResponseEnvelope<InventoryStockItem>> =>
      this.doPostWithAuth(`/inventory-stock-items/for-sale`, request),
  };

  public readonly inventoryTransportLetters: InventoryTransportLettersApi = {
    getAll: (request: GridRequest): Promise<PageResponseEnvelope<InventoryTransportLetter>> =>
      this.doPostWithAuth(`/inventory-transport-letters`, request),
    get: (id: number): Promise<InventoryTransportLetter> =>
      this.doGetWithAuth(`/inventory-transport-letter/${id}`),
    create: (request: SaveInventoryTransportLetterRequest): Promise<number> =>
      this.doPostWithAuth(`/inventory-transport-letter`, request),
    save: (id: number, request: SaveInventoryTransportLetterRequest): Promise<SaveInventoryTransportLetterResponse> =>
      this.doPostWithAuth(`/inventory-transport-letter/${id}`, request),
    delete: (id: number): Promise<void> =>
      this.doDeleteWithAuth(`/inventory-transport-letter/${id}`),

    uploadAttachmentsUrl: (id: number) =>
      `${this.axios.defaults.baseURL}/inventory-transport-letter/${id}/attachments`,

    getAttachments: (id: number) =>
      this.doGetWithAuth(`/inventory-transport-letter/${id}/attachments`),

    deleteAttachment: (id: number, fileName: string) =>
      this.doDeleteWithAuth(`/inventory-transport-letter/${id}/attachments/${fileName}`),

  };

  private readonly axios: AxiosInstance;

  constructor() {
    this.axios = axios.create({
      baseURL: `${Config.getBackendUrl()}/api/v1`
    });
    this.axios.interceptors.response.use(
      response => {
        return response;
      },
      (error: AxiosError) => {
        this.handleGlobalErrors(error);
        return Promise.reject(this.mapAxiosError(error));
      });
  }

  /**
   * Backend interprets months between [1..12], JS is between [0..11]
   */
  private alignMonthIndexes = <T extends YearMonthFilter>(request: T): T => ({
    ...request,
    startMonth: request.startMonth + 1,
    endMonth: request.endMonth + 1,
  });

  /**
   * Backend interprets months between [1..12], JS is between [0..11]
   */
  private alignMonthIndex = <T extends { month?: number }>(params: T): T => (
    params.month === undefined
      ? params
      : ({...params, month: params.month + 1})
  );

  private doMultipartPostWithAuth<T>(url: string, body: FormData): Promise<T> {
    const config: AxiosRequestConfig = {
      headers: {...getAuthorizedConfig().headers, 'Content-Type': 'multipart/form-data'}
    };

    return this.axios
      .post(url, body, config)
      .then((response: AxiosResponse<T>) => {
        return response.data;
      });
  }

  private doArrayBufferGetWithAuth<ArrayBuffer>(url: string): Promise<ArrayBuffer> {
    const config: AxiosRequestConfig = {
      responseType: 'arraybuffer',
      ...getAuthorizedConfig(),
    };

    return this.axios
      .get(url, config)
      .then((response: AxiosResponse<ArrayBuffer>) => {
        return response.data;
      });
  }

  private doPutWithAuth<T>(url: string, body: any): Promise<T> {
    return this.axios
      .put(url, body, getAuthorizedConfig())
      .then((response: AxiosResponse<T>) => {
        return response.data;
      });
  }

  private doPostWithAuth<T>(url: string, body: any): Promise<T> {
    return this.axios
      .post(url, body, getAuthorizedConfig())
      .then((response: AxiosResponse<T>) => {
        return response.data;
      });
  }

  private doGetWithAuth<T>(url: string): Promise<T> {
    return this.axios
      .get(url, getAuthorizedConfig())
      .then((response: AxiosResponse<T>) => {
        return response.data;
      });
  }

  private doNullableGetWithAuth<T>(url: string): Promise<T | null> {
    return this.axios
      .get(url, getAuthorizedConfig())
      .then((response: AxiosResponse<T>) => {
        if ((typeof response.data) === 'string' && (response.data as unknown as string).length === 0) {
          return null;
        }
        return response.data;
      });
  }

  private doDeleteWithAuth<T>(url: string): Promise<T> {
    return this.axios
      .delete(url, getAuthorizedConfig())
      .then((response: AxiosResponse<T>) => {
        return response.data;
      });
  }

  private mapAxiosError(error: AxiosError): ErrorResponse {
    const errorResponse: ErrorResponse = {
      message: error.message
    };
    if (error.response) {
      errorResponse.status = error.response.status;
      errorResponse.details = error.response.data;
    }
    return errorResponse;
  }

  private handleGlobalErrors(error: AxiosError) {
    if (!error.response) {
      return;
    }
    if (!this.globalErrorHandler) {
      return;
    }
    if (error.response.status === 401) {
      this.globalErrorHandler.handle401(error);
    } else if (error.response.status === 403) {
      this.globalErrorHandler.handle403(error);
    }
  }

  private serialize(key: string, value: string) {
    return encodeURIComponent(key) + '=' + encodeURIComponent(value);
  }
}

export default new FuroApi();



// WEBPACK FOOTER //
// ./src/api/FuroApi.ts